import { injectHeadTags } from './headTags';

/**
 * The cookie choice, and what hangs from it.
 *
 * Strictly necessary storage needs no consent and is not asked about. What
 * is asked about is analytics: the owner's Google tag (injected head tags)
 * loads only after an explicit yes, on this visit or a previous one. The
 * choice lives in localStorage — itself listed in the inventory — and can
 * be changed at any time from the banner or the cookie page.
 */

export interface ConsentChoice {
  /** Version of the question asked; a future material change bumps it and re-asks. */
  v: 1;
  analytics: boolean;
  decidedAt: string;
}

const KEY = 'trackilero.consent';

type Listener = () => void;
const listeners = new Set<Listener>();
let askAgain = false;

export function getConsent(): ConsentChoice | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentChoice;
    return parsed && parsed.v === 1 && typeof parsed.analytics === 'boolean' ? parsed : null;
  } catch {
    return null;
  }
}

export function setConsent(analytics: boolean): void {
  const choice: ConsentChoice = { v: 1, analytics, decidedAt: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(choice));
  } catch {
    // A browser that refuses storage will simply be asked again next visit.
  }
  askAgain = false;
  if (analytics) {
    void injectHeadTags();
  } else {
    removeAnalyticsCookies();
  }
  for (const fn of listeners) fn();
}

/** The banner must show: no choice yet, or the person asked to change it. */
export function consentNeeded(): boolean {
  return askAgain || getConsent() === null;
}

/** Reopens the banner, from the cookie page's "change your choice". */
export function reopenConsent(): void {
  askAgain = true;
  for (const fn of listeners) fn();
}

export function subscribeConsent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Loads the owner's head tags only when analytics was said yes to. */
export function initConsentedHeadTags(): void {
  if (getConsent()?.analytics) void injectHeadTags();
}

/**
 * Best-effort removal of the measurement cookies after a no. The script is
 * simply not loaded from here on; this clears what an earlier yes (or the
 * time before the banner existed) left behind.
 */
function removeAnalyticsCookies(): void {
  const hosts = ['', window.location.hostname, `.${window.location.hostname.replace(/^www\./, '')}`];
  for (const part of document.cookie.split(';')) {
    const name = part.split('=')[0]?.trim();
    if (!name || !/^_ga($|_)|^_gid$|^_gat/.test(name)) continue;
    for (const domain of hosts) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain ? `; domain=${domain}` : ''}`;
    }
  }
}
